import { MODAL_TYPE } from 'config/Constants';
import { useStore } from 'hooks/useStore';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { globalWebSocketService } from 'services/globalWebSocketService';
import { useJobPasteWebSocket } from './hooks/useJobPasteWebSocket';

export const useJobPaste = () => {
  const {
    GlobalModalStore,
    JobPasteStore,
    cleanTimeStore,
    roomStore,
    coatingTypeStore,
    cleanTypeStore,
    runTypeStore,
    shiftTimingStore,
    statusCodesStore,
    ScheduleAttainmentStore,
    UserStore,
    RoomEditingStore
  } = useStore();
  const [searchValue, setSearchValue] = useState<string>('');
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const environment = import.meta.env.VITE_ENVIRONMENT;

  const loading =
    (isInitialLoad && JobPasteStore.isJobsLoading) ||
    cleanTimeStore.isLoading ||
    roomStore.isLoading ||
    coatingTypeStore.isLoading ||
    cleanTypeStore.isLoading ||
    runTypeStore.isLoading ||
    shiftTimingStore.isLoading ||
    statusCodesStore.isLoading;

  // Initialize WebSocket connection
  const {
    sendEditingStatus,
    sendActiveUserUpdate,
    isConnected,
    isJobUpdatesConnected,
    isActiveUsersConnected
  } = useJobPasteWebSocket();

  // Initial data fetch function
  const fetchInitialJobs = useCallback(async () => {
    JobPasteStore.setJobsLoading(true);

    try {
      const response = await JobPasteStore.fetchJobsForPolling();
      if (!response) {
        return;
      }
      const { rooms } = response;

      if (JobPasteStore.isEditMode) {
        // Case 2: Edit mode - compare and append new jobs
        JobPasteStore.updateJobsOptimized(rooms);
      } else {
        // Case 1: Normal mode - replace all jobs
        JobPasteStore.updateJobsNormal(rooms);
      }
    } catch (error) {
      if (environment === 'development') {
        console.error('Error fetching initial jobs:', error);
      }
      toast.error('Error fetching initial jobs');
    } finally {
      JobPasteStore.setJobsLoading(false);
      // Mark initial load as complete after first data fetch
      if (isInitialLoad) {
        setIsInitialLoad(false);
      }
    }
  }, [JobPasteStore, isInitialLoad]);

  // Load reference data and initial jobs on component mount
  useEffect(() => {
    // Load reference data
    cleanTimeStore.fetchCleanTimes(1, 100);
    roomStore.fetchRooms(1, 100);
    coatingTypeStore.fetchCoatingTypes(1, 100);
    cleanTypeStore.fetchCleanTypes(1, 100);
    runTypeStore.fetchRunTypes(1, 100);
    shiftTimingStore.fetchShiftTimings(1, 100);
    statusCodesStore.fetchStatusCodes(1, 100);

    if (UserStore.userDepartment) {
      statusCodesStore.fetchPossibleStatusTransitions(UserStore.userDepartment);
    }

    // Initialize data with shared filter
    JobPasteStore.initializeWithSharedFilter(shiftTimingStore);

    // Fetch initial jobs data
    fetchInitialJobs();

    // Set UserStore for globalWebSocketService
    globalWebSocketService.setUserStore(UserStore);

    // Initialize job-updates WebSocket connection when JobPaste component mounts
    // Check if connection already exists before creating a new one
    if (!globalWebSocketService.isConnected) {
      globalWebSocketService.connect();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup WebSocket connections on unmount
  useEffect(() => {
    return () => {
      // Disconnect job-updates WebSocket when JobPaste component unmounts
      globalWebSocketService.disconnect();
      RoomEditingStore.cleanup();
    };
  }, [RoomEditingStore]);

  // Prepare room options from roomStore
  const roomOptions = roomStore.rooms.map(room => ({
    label: `${room.room_long}`,
    code: room.room_short
  }));

  const scheduleOptions = shiftTimingStore.shiftTimings.map(item => ({
    label: `${item.shift_name}`,
    code: item.shift_name
  }));

  // Handle modal open
  const handleModalPOpen = () => {
    GlobalModalStore.toggleModal(MODAL_TYPE.PPS_JPB_PASTE_ADD_JOB_MODAL);
  };

  const handleScheduleAttainmnetModalOpen = () => {
    GlobalModalStore.toggleModal(MODAL_TYPE.PPS_JOB_PASTE_SCHEDULE_ATTAINMENT_MODAL);
  };

  const handleImportSysproJobsModalOpen = () => {
    GlobalModalStore.toggleModal(MODAL_TYPE.PPS_JOB_PASTE_IMPORT_SYSPRO_JOBS_MODAL);
  };

  // Update search query with debounce
  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      JobPasteStore.setSearchQuery(searchValue);
    }, 300); // 300ms debounce

    return () => clearTimeout(debounceTimeout);
  }, [searchValue, JobPasteStore]);

  // Set shift and date directly from store
  const setShift = (shift: string | null) => {
    JobPasteStore.setShift(shift);
  };

  const setDate = (date: Date) => {
    JobPasteStore.setDate(date);
  };

  return {
    searchValue,
    setSearchValue,
    loading,
    setShift,
    setDate,
    shift: JobPasteStore.shift,
    date: JobPasteStore.date,
    handleModalPOpen,
    handleScheduleAttainmnetModalOpen,
    handleImportSysproJobsModalOpen,
    scheduleOptions,
    roomOptions,
    shiftTimingStore,
    ScheduleAttainmentStore,
    isInitialLoad,
    sendEditingStatus,
    sendActiveUserUpdate,
    isConnected,
    isJobUpdatesConnected,
    isActiveUsersConnected
  };
};
